// =============================================================================
// VINUM - Storage Configuration
// Allows configuring platform-specific storage adapters
// =============================================================================

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface StorageAdapter {
  getItem: (key: string) => Promise<string | null> | string | null;
  setItem: (key: string, value: string) => Promise<void> | void;
  removeItem: (key: string) => Promise<void> | void;
}

// -----------------------------------------------------------------------------
// Default Storage (localStorage with fallback)
// -----------------------------------------------------------------------------

const memoryStorage: Record<string, string> = {};

const defaultStorage: StorageAdapter = {
  getItem: (key: string) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return memoryStorage[key] ?? null;
  },
  setItem: (key: string, value: string) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
    } else {
      memoryStorage[key] = value;
    }
  },
  removeItem: (key: string) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(key);
    } else {
      delete memoryStorage[key];
    }
  },
};

// -----------------------------------------------------------------------------
// Storage Instance
// -----------------------------------------------------------------------------

let storageAdapter: StorageAdapter = defaultStorage;

/**
 * Configure the storage adapter for the platform
 * Call this early in app initialization before any stores are accessed
 *
 * @example
 * // React Native with expo-secure-store
 * import * as SecureStore from 'expo-secure-store';
 *
 * configureStorage({
 *   getItem: SecureStore.getItemAsync,
 *   setItem: SecureStore.setItemAsync,
 *   removeItem: SecureStore.deleteItemAsync,
 * });
 */
export function configureStorage(adapter: StorageAdapter): void {
  storageAdapter = adapter;
}

/**
 * Get the current storage adapter
 */
export function getStorage(): StorageAdapter {
  return storageAdapter;
}

/**
 * Create a Zustand-compatible storage object
 */
export function createZustandStorage() {
  return {
    getItem: async (name: string): Promise<string | null> => {
      const result = storageAdapter.getItem(name);
      return result instanceof Promise ? result : Promise.resolve(result);
    },
    setItem: async (name: string, value: string): Promise<void> => {
      const result = storageAdapter.setItem(name, value);
      return result instanceof Promise ? result : Promise.resolve();
    },
    removeItem: async (name: string): Promise<void> => {
      const result = storageAdapter.removeItem(name);
      return result instanceof Promise ? result : Promise.resolve();
    },
  };
}
